import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import ApacCsF5 from "../Figures/ApacCsF5";
import useAreaQuery from "../../../../hooks/useAreaQuery";
import {
  formatNumberAsHumanReadable,
  formatPercentage,
  getQualitativeChangeDirection,
  getQualitativeChangeMagnitude,
} from "../../../../utilities";

const calculateChange = (years) => {
  if (!years) {
    return {
      initial: 0,
      final: 0,
      change: 0,
      percentageChange: 0,
    };
  }

  const initial = years[0].services;
  const final = years[2].services;
  const change = final - initial;

  return {
    initial,
    final,
    change,
    percentageChange: (change / initial) * 100,
  };
};

const calculateMetrics = (data) => ({
  surgicalAspirationServicesUnderScenario1: calculateChange(
    data?.[0].scenarios[0].years
  ),
  medicalServicesUnderScenario1: calculateChange(data?.[1].scenarios[0].years),
  surgicalAspirationServicesUnderScenario2: calculateChange(
    data?.[0].scenarios[1].years
  ),
  medicalServicesUnderScenario2: calculateChange(data?.[1].scenarios[1].years),
  surgicalAspirationServicesUnderScenario3: calculateChange(
    data?.[0].scenarios[2].years
  ),
  medicalServicesUnderScenario3: calculateChange(data?.[1].scenarios[2].years),
  misoMifeMedicalServicesUnderScenario3: calculateChange(
    data?.[3].scenarios[2].years
  ),
  misoMedicalServicesUnderScenario3: calculateChange(
    data?.[2].scenarios[2].years
  ),
});

const useStyles = makeStyles((theme) => ({
  list: {
    "& li": {
      margin: theme.spacing(2, 0),
    },
  },
}));

const ApacCsS6 = (props) => {
  const { areaData: data } = useAreaQuery(
    `/custom/apac/cs/figures/5/${props.regionId}`
  );

  const classes = useStyles();

  const metrics = calculateMetrics(data);

  return (
    <>
      <p>
        Between 2019 and 2030, different trends in costed abortion and PAC
        services are seen by method across the three scenarios (Figure 5).
      </p>

      <ul className={classes.list}>
        <li>
          With no changes in the safety profile or method mix (Scenario 1), the
          number of surgical aspiration services would see a{" "}
          {getQualitativeChangeMagnitude(
            metrics.surgicalAspirationServicesUnderScenario1.percentageChange
          )}{" "}
          {getQualitativeChangeDirection(
            metrics.surgicalAspirationServicesUnderScenario1.percentageChange
          )}{" "}
          from{" "}
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario1.initial
          )}{" "}
          in 2019 to{" "}
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario1.final
          )}{" "}
          in 2030 (
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario1.change,
            {
              prefixWithSign: true,
            }
          )}
          ,{" "}
          {formatPercentage(
            metrics.surgicalAspirationServicesUnderScenario1.percentageChange,
            {
              prefixWithSign: true,
            }
          )}
          ); and the number of medical services would see a{" "}
          {getQualitativeChangeMagnitude(
            metrics.medicalServicesUnderScenario1.percentageChange
          )}{" "}
          {getQualitativeChangeDirection(
            metrics.medicalServicesUnderScenario1.percentageChange
          )}{" "}
          from{" "}
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario1.initial
          )}{" "}
          in 2019 to{" "}
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario1.final
          )}{" "}
          in 2030 (
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario1.change,
            {
              prefixWithSign: true,
            }
          )}
          ,{" "}
          {formatPercentage(
            metrics.medicalServicesUnderScenario1.percentageChange,
            {
              prefixWithSign: true,
            }
          )}
          ).
        </li>

        <li>
          With a shift in safety profile (Scenario 2), the number of surgical
          aspiration services would see a{" "}
          {getQualitativeChangeMagnitude(
            metrics.surgicalAspirationServicesUnderScenario2.percentageChange
          )}{" "}
          {getQualitativeChangeDirection(
            metrics.surgicalAspirationServicesUnderScenario2.percentageChange
          )}{" "}
          from{" "}
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario2.initial
          )}{" "}
          in 2019 to{" "}
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario2.final
          )}{" "}
          in 2030 (
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario2.change,
            {
              prefixWithSign: true,
            }
          )}
          ,{" "}
          {formatPercentage(
            metrics.surgicalAspirationServicesUnderScenario2.percentageChange,
            {
              prefixWithSign: true,
            }
          )}
          ); and the number of medical services would see a{" "}
          {getQualitativeChangeMagnitude(
            metrics.medicalServicesUnderScenario2.percentageChange
          )}{" "}
          {getQualitativeChangeDirection(
            metrics.medicalServicesUnderScenario2.percentageChange
          )}{" "}
          from{" "}
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario2.initial
          )}{" "}
          in 2019 to{" "}
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario2.final
          )}{" "}
          in 2030 (
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario2.change,
            {
              prefixWithSign: true,
            }
          )}
          ,{" "}
          {formatPercentage(
            metrics.medicalServicesUnderScenario2.percentageChange,
            {
              prefixWithSign: true,
            }
          )}
          ).
        </li>

        <li>
          With a shift in safety profile and method mix (Scenario 3), the number
          of surgical aspiration services would see a{" "}
          {getQualitativeChangeMagnitude(
            metrics.surgicalAspirationServicesUnderScenario3.percentageChange
          )}{" "}
          {getQualitativeChangeDirection(
            metrics.surgicalAspirationServicesUnderScenario3.percentageChange
          )}{" "}
          from{" "}
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario3.initial
          )}{" "}
          in 2019 to{" "}
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario3.final
          )}{" "}
          in 2030 (
          {formatNumberAsHumanReadable(
            metrics.surgicalAspirationServicesUnderScenario3.change,
            {
              prefixWithSign: true,
            }
          )}
          ,{" "}
          {formatPercentage(
            metrics.surgicalAspirationServicesUnderScenario3.percentageChange,
            {
              prefixWithSign: true,
            }
          )}
          ) ; and the number of medical services would see a{" "}
          {getQualitativeChangeMagnitude(
            metrics.medicalServicesUnderScenario3.percentageChange
          )}{" "}
          {getQualitativeChangeDirection(
            metrics.medicalServicesUnderScenario3.percentageChange
          )}{" "}
          from{" "}
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario3.initial
          )}{" "}
          in 2019 to{" "}
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario3.final
          )}{" "}
          in 2030 (
          {formatNumberAsHumanReadable(
            metrics.medicalServicesUnderScenario3.change,
            {
              prefixWithSign: true,
            }
          )}
          ,{" "}
          {formatPercentage(
            metrics.medicalServicesUnderScenario3.percentageChange,
            {
              prefixWithSign: true,
            }
          )}
          ). Under this scenario, the number of medical services using
          misoprostol and mifepristone combined would see a{" "}
          {getQualitativeChangeMagnitude(
            metrics.misoMifeMedicalServicesUnderScenario3.percentageChange
          )}{" "}
          {getQualitativeChangeDirection(
            metrics.misoMifeMedicalServicesUnderScenario3.percentageChange
          )}{" "}
          from{" "}
          {formatNumberAsHumanReadable(
            metrics.misoMifeMedicalServicesUnderScenario3.initial
          )}{" "}
          in 2019 to{" "}
          {formatNumberAsHumanReadable(
            metrics.misoMifeMedicalServicesUnderScenario3.final
          )}{" "}
          in 2030 (
          {formatNumberAsHumanReadable(
            metrics.misoMifeMedicalServicesUnderScenario3.change,
            {
              prefixWithSign: true,
            }
          )}
          ,{" "}
          {formatPercentage(
            metrics.misoMifeMedicalServicesUnderScenario3.percentageChange,
            {
              prefixWithSign: true,
            }
          )}
          ) while medical services using misoprostol only would see a{" "}
          {getQualitativeChangeMagnitude(
            metrics.misoMedicalServicesUnderScenario3.percentageChange
          )}{" "}
          {getQualitativeChangeDirection(
            metrics.misoMedicalServicesUnderScenario3.percentageChange
          )}{" "}
          from{" "}
          {formatNumberAsHumanReadable(
            metrics.misoMedicalServicesUnderScenario3.initial
          )}{" "}
          in 2019 to{" "}
          {formatNumberAsHumanReadable(
            metrics.misoMedicalServicesUnderScenario3.final
          )}{" "}
          in 2030 (
          {formatNumberAsHumanReadable(
            metrics.misoMedicalServicesUnderScenario3.change,
            {
              prefixWithSign: true,
            }
          )}
          ,{" "}
          {formatPercentage(
            metrics.misoMedicalServicesUnderScenario3.percentageChange,
            {
              prefixWithSign: true,
            }
          )}
          ).
        </li>
      </ul>

      {data && (
        <ApacCsF5
          regionId={props.regionId}
          regionName={props.regionName}
          data={data}
        />
      )}
    </>
  );
};

ApacCsS6.propTypes = {
  regionId: PropTypes.number.isRequired,
  regionName: PropTypes.string.isRequired,
};

export default ApacCsS6;
